// 圖片壓縮：超過目標大小的圖片以 canvas 重新編碼成 JPEG，
// 優先維持原始解析度並找出不超過目標大小的最高品質，品質降到下限仍超過才縮小尺寸。
const MAX_QUALITY = 1; // 起始(最高)品質，在目標大小內盡量用最高品質
const MIN_QUALITY = 0.75; // 品質下限，再低畫質會明顯劣化，改用縮小尺寸
const QUALITY_SEARCH_STEPS = 5; // 品質二分搜尋次數
const MAX_RESIZE_ATTEMPTS = 5; // 縮小尺寸重試次數
const SKIP_TYPES = ['image/gif']; // GIF 可能是動畫，轉檔會失去動畫效果，不壓縮

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('圖片讀取失敗'));
    };
    img.src = url;
  });
}

function drawToCanvas(img, scale) {
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
  canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
  const ctx = canvas.getContext('2d');
  // JPEG 不支援透明，先鋪白底避免 PNG 透明區域變黑
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  return canvas;
}

function canvasToJpeg(canvas, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error('圖片壓縮失敗'));
      }
    }, 'image/jpeg', quality);
  });
}

// 二分搜尋不超過 maxBytes 的最高品質 (low 品質的結果 best 已確定符合大小)
function searchQuality(canvas, maxBytes, low, high, best, stepsLeft) {
  if (stepsLeft === 0) return Promise.resolve(best);
  const mid = (low + high) / 2;
  return canvasToJpeg(canvas, mid).then((blob) => {
    if (blob.size <= maxBytes) return searchQuality(canvas, maxBytes, mid, high, blob, stepsLeft - 1);
    return searchQuality(canvas, maxBytes, low, mid, best, stepsLeft - 1);
  });
}

// 在目前尺寸下壓縮，fits 表示是否已壓到 maxBytes 以內
function encodeWithinSize(canvas, maxBytes) {
  return canvasToJpeg(canvas, MAX_QUALITY).then((highest) => {
    if (highest.size <= maxBytes) return { blob: highest, fits: true };
    return canvasToJpeg(canvas, MIN_QUALITY).then((lowest) => {
      if (lowest.size > maxBytes) return { blob: lowest, fits: false };
      return searchQuality(canvas, maxBytes, MIN_QUALITY, MAX_QUALITY, lowest, QUALITY_SEARCH_STEPS)
        .then((blob) => ({ blob, fits: true }));
    });
  });
}

function compressAtScale(img, maxBytes, scale, attemptsLeft) {
  const canvas = drawToCanvas(img, scale);
  return encodeWithinSize(canvas, maxBytes).then(({ blob, fits }) => {
    // 釋放 canvas 記憶體
    canvas.width = 0;
    canvas.height = 0;
    if (fits || attemptsLeft === 0) return blob;
    // 檔案大小約與像素數成正比，依超出比例縮小尺寸後重試
    const nextScale = scale * Math.sqrt(maxBytes / blob.size) * 0.95;
    return compressAtScale(img, maxBytes, nextScale, attemptsLeft - 1);
  });
}

function toJpegFileName(name) {
  const dotIndex = name.lastIndexOf('.');
  return `${dotIndex > 0 ? name.slice(0, dotIndex) : name}.jpg`;
}

// 將圖片壓縮到 maxBytes 以內，未超過大小或為 GIF 時直接回傳原檔
export function compressImage(file, maxBytes) {
  if (file.size <= maxBytes || SKIP_TYPES.includes(file.type)) return Promise.resolve(file);
  return loadImage(file)
    .then((img) => compressAtScale(img, maxBytes, 1, MAX_RESIZE_ATTEMPTS))
    .then((blob) => {
      if (blob.size >= file.size) return file; // 壓縮後沒有變小就保留原檔
      return new File([blob], toJpegFileName(file.name), { type: 'image/jpeg', lastModified: file.lastModified });
    });
}

// 等待列表中所有圖片壓縮完成 (compressTask 由 FileUpload 設定)
export function waitForCompression(fileList) {
  return Promise.all(fileList.map((file) => file.compressTask));
}

export function formatFileSize(bytes) {
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}
